import { Mat4, Quat, Vec3 } from 'playcanvas';

import { Element, ElementType } from './element';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import { SceneCameraAnimTrack } from './scene-camera-anim-track';
import { Scene } from './scene';

type SceneCameraData = {
    animTrack: SceneCameraAnimTrack;
    lookThrough: boolean;
    fov: number;
    aspect: number;
};

// Module-level singleton tracking which scene camera is currently in look-through mode.
let activeLookThroughSceneCamera: HelperPoint | null = null;

const isSceneCamera = (element: Element): boolean => {
    return element?.type === ElementType.helper && !!(element as any).sceneCameraData;
};

const getSceneCameraData = (element: Element): SceneCameraData | null => {
    if (!isSceneCamera(element)) return null;
    return (element as any).sceneCameraData as SceneCameraData;
};

/**
 * Get a unique "Camera N" name by scanning existing scene cameras.
 */
const getUniqueCameraName = (scene: Scene): string => {
    const helpers = scene.getElementsByType(ElementType.helper);
    let max = 0;
    for (const h of helpers) {
        if (!(h as any).sceneCameraData) continue;
        const match = (h as any).name?.match(/^Camera\s*(\d+)$/i);
        if (match) {
            const n = Number(match[1]);
            if (n > max) max = n;
        }
    }
    return `Camera ${max + 1}`;
};

/**
 * Create a new scene camera. Positioned at the current editor camera location.
 * If a parent helper is provided, the editor camera world pose is converted
 * to parent-local space.
 */
const createSceneCamera = async (
    scene: Scene,
    events: Events,
    name?: string,
    parent?: HelperPoint | null
): Promise<HelperPoint> => {
    const camName = name ?? getUniqueCameraName(scene);

    const helper = new HelperPoint({
        name: camName,
        animated: false,
        visible: true,
        parentEntity: parent ? parent.entity : null
    });

    // Establish logical hierarchy before add so UI indentation is correct on first paint.
    if (parent) {
        helper.parentElement = parent;
        parent.childElements.push(helper as any);
    }

    // Position at current editor camera
    const pose = events.invoke('camera.getPose') as { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } } | undefined;
    if (pose) {
        const worldPos = new Vec3(pose.position.x, pose.position.y, pose.position.z);
        const worldTarget = new Vec3(pose.target.x, pose.target.y, pose.target.z);

        if (parent) {
            // Convert world pose to parent-local space
            const invWorld = parent.entity.getWorldTransform().clone().invert();
            invWorld.transformPoint(worldPos, worldPos);
            invWorld.transformPoint(worldTarget, worldTarget);
        }

        helper.entity.setLocalPosition(worldPos);

        // Orient to look at the target
        if (worldTarget.distance(worldPos) > 1e-6) {
            const mat = new Mat4();
            mat.setLookAt(worldPos, worldTarget, Vec3.UP);
            const rot = new Quat();
            rot.setFromMat4(mat);
            helper.entity.setLocalRotation(rot);
        }
    }

    // Create per-camera anim track
    const animTrack = new SceneCameraAnimTrack(events, helper.entity);

    const data: SceneCameraData = {
        animTrack,
        lookThrough: false,
        fov: 60,
        aspect: 16 / 9
    };

    (helper as any).sceneCameraData = data;

    await scene.add(helper as any);
    events.fire('hierarchy.changed', { child: helper as any, oldParent: null, newParent: parent as any ?? null });

    return helper;
};

/**
 * Clear any active scene camera look-through. Exported for cross-system
 * coordination with CameraMotionController.
 */
const clearSceneCameraLookThrough = () => {
    if (activeLookThroughSceneCamera) {
        const data = (activeLookThroughSceneCamera as any).sceneCameraData as SceneCameraData | undefined;
        if (data) {
            data.lookThrough = false;
        }
        activeLookThroughSceneCamera = null;
    }
};

const getActiveLookThroughSceneCamera = () => {
    return activeLookThroughSceneCamera;
};

const sampleActiveSceneCameraLookThroughPose = (frame: number) => {
    if (!activeLookThroughSceneCamera) {
        return null;
    }

    const helper = activeLookThroughSceneCamera;
    const data = (helper as any).sceneCameraData as SceneCameraData | undefined;
    if (!data?.lookThrough) {
        return null;
    }

    const result = data.animTrack.evaluate(frame);
    if (result) {
        return result;
    }

    const pos = helper.entity.getPosition();
    const rot = helper.entity.getRotation();
    const fwd = new Vec3();
    rot.transformVector(Vec3.FORWARD, fwd);
    return {
        position: pos.clone(),
        target: new Vec3().add2(pos, fwd)
    };
};

/**
 * Toggle look-through for a scene camera. Only one camera (across both
 * scene cameras and imported camera motion) can be in look-through at a time.
 */
const setSceneCameraLookThrough = (helper: HelperPoint, events: Events, value: boolean) => {
    const data = (helper as any).sceneCameraData as SceneCameraData | undefined;
    if (!data) return;

    if (!value) {
        data.lookThrough = false;
        if (activeLookThroughSceneCamera === helper) {
            activeLookThroughSceneCamera = null;
        }
        events.fire('cameraPlayback.refresh');
        return;
    }

    // Disable any other scene camera look-through
    if (activeLookThroughSceneCamera && activeLookThroughSceneCamera !== helper) {
        clearSceneCameraLookThrough();
    }

    // Disable any camera motion controller look-through (cross-system).
    // The CameraMotionController module listens for this event in its doc registration.
    events.fire('cameraMotion.clearLookThrough');

    activeLookThroughSceneCamera = helper;
    data.lookThrough = true;
    events.fire('cameraPlayback.refresh');
};

/**
 * Clean up scene camera data when the helper is being removed.
 */
const destroySceneCameraData = (helper: HelperPoint, events: Events) => {
    const data = (helper as any).sceneCameraData as SceneCameraData | undefined;
    if (!data) return;

    if (data.lookThrough) {
        setSceneCameraLookThrough(helper, events, false);
    }

    data.animTrack.clear();
    (helper as any).sceneCameraData = null;
};

/**
 * Register lifecycle listeners for scene cameras.
 * Call once during app init (after scene is created).
 */
const registerSceneCameraEvents = (scene: Scene, events: Events) => {
    events.on('scene.elementRemoved', (element: Element) => {
        if (isSceneCamera(element)) {
            destroySceneCameraData(element as any as HelperPoint, events);
        }
    });

    events.on('scene.clear', () => {
        clearSceneCameraLookThrough();
    });

    // Cross-system: camera motion controller fires this when activating its look-through
    events.on('sceneCamera.clearLookThrough', () => {
        clearSceneCameraLookThrough();
    });
};

export {
    createSceneCamera,
    isSceneCamera,
    getSceneCameraData,
    getActiveLookThroughSceneCamera,
    sampleActiveSceneCameraLookThroughPose,
    setSceneCameraLookThrough,
    clearSceneCameraLookThrough,
    destroySceneCameraData,
    registerSceneCameraEvents
};

export type { SceneCameraData };
